<template>
  <section>
    <input
      type="hidden"
      :data-vv-scope="validationScope"
      :name="'test-drive-selection'"
      :value="validationKey"
      v-validate.continues="'test-drive-selection'"/>

    <DatePickerComponent
      v-if="showComponent"
      :label="dateLabel"
      :hideLabel="hideDateLabel"
      :placeholder="datePlaceholder"
      :amountDaysLimit="daysLimit"
      :daysToSkip="daysToSkip"
      :disabledDates="disabledDates"
      :showOnlyFutureDates="true"
      :defaultCalendarOpen="defaultCalendarOpen"
      @updateDate="onDateUpdated"
    />

    <TimePickerComponent
      v-if="showComponent"
      :label="timeLabel"
      :hideLabel="hideTimeLabel"
      :placeholder="timePlaceholder"
      :date="selectedDate"
      :timeSlots="timeSlots"
      :disabled="!selectedDate"
      :defaultTimepickerOpen="defaultTimepickerOpen && selectedDate"
      @input="onTimeUpdated"
     />

    <span
      v-if="showComponent"
      class="tla-form-group__message tla-test-drive-validation"
      :class="getValidationClass('test-drive-selection', 'message')">
      Please select a preferred date and time.
    </span>
  </section>
</template>

<script lang="ts">
import TimeSlot from '@/models/date-picker/TimeSlot';
import {
  Vue, Component, Prop, Inject, Watch
} from 'vue-property-decorator';
import IAvailabilityData from '@/models/test-drive/interfaces/availability-data.d';
import DateUtils from '@/utils/DateUtils';
import SelectedTimeSlot from '@/models/date-picker/SelectedTimeSlot';
import IRetailerVehicleAvailability from '@/models/test-drive/interfaces/retailer-vehicle-availability.d';
import { Validator } from 'vee-validate';
import { validationService } from '@/services/ValidationService';
import IVehicle from '@/models/vehicle/interfaces/vehicle.d';
import TimePickerComponent from '../ui/TimePickerComponent.vue';
import DatePickerComponent from '../ui/DatePickerComponent.vue';

Validator.extend('test-drive-selection', {
  getMessage: (field: string) => 'Please enter all required fields',
  validate: (value: string) => validationService.isValidTestDriveSlot()
});

@Component({
  components: {
  DatePickerComponent,
  TimePickerComponent
  }
  })
export default class TestDriveBookingComponent extends Vue {
  @Inject('$validator') public $validator!: Validator;

  @Prop({ default: 'Select your preferred date' })
  public dateLabel!: string;

  @Prop({ default: 'Please choose a date...' })
  public datePlaceholder!: string;

  @Prop({ default: 'Select your preferred time' })
  public timeLabel!: string;

  @Prop({ default: 'Please choose a time...' })
  public timePlaceholder!: string;

  @Prop({ default: 30 })
  public daysLimit!: number;

  @Prop({ default: null })
  public validationScope!: string;

  @Prop({ default: false })
  public disableSkipStep!: boolean;

  @Prop({ default: false })
  public defaultCalendarOpen!: boolean;

  @Prop({ default: false })
  public defaultTimepickerOpen!: boolean;

  @Prop({ default: false })
  public hideDateLabel!: boolean;

  @Prop({ default: false })
  public hideTimeLabel!: boolean;

  public stepNumber = 0;

  public selectedDate: Date | null = null;

  public selectedTimeSlot: SelectedTimeSlot | null = null;

  public get currentStep(): number {
    return this.$store.getters['enquiryModule/currentStep'];
  }

  public get retailerAvailability(): IAvailabilityData[] {
    return this.$store.getters['testDriveAvailabilityModule/retailerAvailability'];
  }

  public get retailer(): IRetailerVehicleAvailability | null {
    return this.$store.getters['retailerModule/selectedRetailer'];
  }

  public submitAppointment(slot: SelectedTimeSlot | null) {
    this.$store.dispatch('testDriveAvailabilityModule/submitAppointment', slot);
  }

  public async submitRetrieveRetailerAvailability() {
    await this.$store.dispatch('testDriveAvailabilityModule/submitRetrieveRetailerAvailability');
  }

  public get testDriveRequested(): boolean {
    return this.$store.getters['enquiryModule/testDriveRequested'];
  }

  public get currentVehicle(): IVehicle | null {
    return this.$store.getters['vehicleModule/currentVehicle'];
  }

  public get blockedRangeIds(): number[] {
    return this.$store.getters['testDriveAvailabilityModule/blockedRangeIds'];
  }

  public get allowedTestDriveManufacturerIds(): number[] {
    return this.$store.getters['testDriveAvailabilityModule/allowedManufacturerIds'];
  }

  public get noResults(): boolean {
    return this.$store.getters['testDriveAvailabilityModule/noResults'];
  }

  public get isValidVehicle(): boolean {
    return this.currentVehicle !== null
      && this.allowedTestDriveManufacturerIds.includes(this.currentVehicle.manufacturerId)
      && !this.blockedRangeIds.includes(this.currentVehicle.rangeId);
  }

  public get showComponent(): boolean {
    return this.retailerAvailability
      && this.retailer !== null
      && this.retailer.retailerId > 0
      && this.isValidVehicle;
  }

  public onDateUpdated(date: Date) {
    this.selectedDate = date;
  }

  public onTimeUpdated(slot: SelectedTimeSlot) {
    this.selectedTimeSlot = slot;
    this.submitAppointment(slot);
  }

  public get availableDateObjects(): Date[] {
    return this.retailerAvailability.filter((d) => d.availableSlots > 0 && d.availableDate > new Date() && d.availableDate < this.endDate)
      .map((d) => d.availableDate);
  }

  public get daysToSkip(): number {
    return this.availableDateObjects.length > 0 ? 1 : 3;
  }

  public get startDate(): Date {
    return DateUtils.addDays(this.daysToSkip);
  }

  public get endDate(): Date {
    return DateUtils.addDays(this.daysLimit);
  }

  public get disabledDates(): Date[] {
    const disabled: Date[] = [];

    if (this.availableDateObjects.length === 0) {
      return disabled;
    }

    for (let d = this.startDate; d <= this.endDate; d.setDate(d.getDate() + 1)) {
      const match = this.availableDateObjects.find((m) => DateUtils.isSameDay(m, d));

      if (!match) {
        disabled.push(new Date(d));
      }
    }

    return disabled;
  }

  public get shouldNotSkip(): boolean {
    return this.disableSkipStep
    || (this.testDriveRequested
    && this.currentVehicle !== null
    && this.allowedTestDriveManufacturerIds.includes(this.currentVehicle.manufacturerId)
    && !this.blockedRangeIds.includes(this.currentVehicle.rangeId)
    && !this.noResults);
  }

  public get vehicleRetailerKey(): string {
    return `${this.currentVehicle ? this.currentVehicle.rangeId : 0}-${this.testDriveRequested ? 'yes' : 'no'}-${this.noResults ? 'yes' : 'no'}-${this.retailer ? this.retailer.retailerId : 0}`;
  }

  public get validationKey(): string {
    return `${this.vehicleRetailerKey}-${this.selectedTimeSlot ? this.selectedTimeSlot.dateFormatted : 'na'}-${this.selectedTimeSlot ? this.selectedTimeSlot.startEndTimeFormatted : 'na'}`;
  }

  public setOrRemoveSkipNextStep() {
    if (this.stepNumber < 1) {
      return;
    }

    const nextStep = this.stepNumber + 1;

    if (this.shouldNotSkip) {
      this.$store.dispatch('enquiryModule/submitRemoveSkippedStepId', nextStep);
    } else {
      this.$store.dispatch('enquiryModule/submitSkippedStepId', nextStep);
    }
  }

  @Watch('validationKey')
  public validateSelectedOptions() {
    this.setOrRemoveSkipNextStep();
    this.$validator.validateAll(this.validationScope);
  }

  @Watch('vehicleRetailerKey')
  public async vehicleRetailerChange() {
    if (this.isValidVehicle) {
      await this.submitRetrieveRetailerAvailability();
    }
  }

  public getValidationClass(fieldName: string, fieldType: string) {
    return validationService
      .getValidationClass(this.$validator, fieldName, fieldType, false, false, this.validationScope);
  }

  public get timeSlots(): TimeSlot[] {
    if (this.selectedDate) {
      return DateUtils.getTimeSlots(this.selectedDate);
    }

    return [];
  }

  public async created() {
    this.stepNumber = this.currentStep;
    this.setOrRemoveSkipNextStep();
    this.vehicleRetailerChange();
  }
}
</script>
